import * as yup from 'yup';
import dayjs from "dayjs";
import {citySchema, houseNumberSchema, streetNameSchema} from "./ValidationCommonSchemas";
import { useTenantStore } from "../stores/tenant";
import { isVonoviaBlockedEmail, VONOVIA_BLOCKED_EMAIL_ERROR, DELIVERY_TYPE } from './constants';

const getTenantStore = () => useTenantStore(); // there is an error if you define store in the global scope, so you need to define it in a function

const maxDigitsMessage = 'Bitte verwenden Sie nur maximal zwei Zahlen nach dem Dezimaltrennzeichen. (z.B. 1200,30)';
const depositFormatMessage = 'Bitte Verwenden Sie bitte keinen Punkt (.) als Trennzeichen für Dezimalstellen oder Tausender. z.B. 1500,50';
const rentFormatMessage = 'Für Dezimalstellen verwenden Sie bitte das Komma (,) als Trennzeichen. Verwenden ' +
    'Sie bitte keinen Punkt (.) als Trennzeichen für Dezimalstellen oder Tausender. Sollte Ihre Mietkaution ' +
    '1.500,50 Euro betragen, geben Sie bitte 1500,50 ein.';


const depositAmountSchema = yup.string()
    .typeError(depositFormatMessage)
    .test(
        'is-number',
        'Die Kaution muss eine Zahl sein.',
        (value) => {
            const re = /^[0-9,.]*$/;
            return re.test(String(value));
        })
    .test(
        'maxDigitsAfterComma',
        maxDigitsMessage,
        value => {
            const splitted = value.split(',');
            return splitted.length === 1 || splitted[1].length <= 2;
        })
    .test(
        'is-more-or-equal-to-300',
        'Die Kautionssumme muss mindestens 300 Euro betragen.',
        (value) => {
            return parseFloat(value.replace(',', '.')) >= 300;
        })
    .test(
        'is-valid-deposit-amount',
        'Die Kautionssumme darf maximal das dreifache der Nettokaltmiete betragen.',
        (value) => {
            const tenantStore = getTenantStore();
            const rentAmount = tenantStore.rentAmountAsNumber;
            if (!rentAmount) { // wenn es keine Miete gibt, soll dieser Test nicht fehlschlagen
                return true;
            }
            const depositAmount = parseFloat(value.replace(',', '.'));
            return depositAmount <= rentAmount * 3;
        })
    .test(
        'is-less-or-equal-to-7500',
        'Die Kautionssumme darf maximal 7500 Euro betragen.',
        (value) => {
            return parseFloat(value.replace(',', '.')) <= 7500;
        })
    .test(
        'is-valid-decimal-separator',
        depositFormatMessage,
        (value) => !(value).match(/[.]/)
    );

const landlordEmailSchema = yup.string()
    .email('Bitte geben Sie eine gültige E-Mail-Adresse ein.')
    .nullable()
    .test(
        'no-special-chars',
        'Die E-Mail-Adresse des Vermieters darf keine Umlaute oder andere Sonderzeichen enthalten.',
        (value) => {
            const re = /[üößä]/;
            return !re.test(String(value)
                .toLowerCase());
        }
    )
    .test(
        'is-not-same-as-tenant',
        'Die E-Mail-Adresse des Vermieters sollte nicht mit der des Mieters übereinstimmen.',
        (value) => {
            const tenantStore = getTenantStore();
            const tenantEmail = tenantStore.formValues.start_email;
            return tenantEmail !== value;
        })
    .test(
        'no-vonovia-blocked-email',
        VONOVIA_BLOCKED_EMAIL_ERROR,
        (value) => {
            const tenantStore = getTenantStore();
            return !isVonoviaBlockedEmail(value) || tenantStore.formValues.next_destAddress !== DELIVERY_TYPE.DIGITAL;
        })
    .test(
        'required-if-digital',
        'Die E-Mail des Vermieters ist für die digitale Bürgschaft erforderlich',
        (value) => {
            const tenantStore = getTenantStore();
            const destination = tenantStore.formValues.next_destAddress;
            return destination !== DELIVERY_TYPE.DIGITAL || value !== '';
        })
    .max(255, 'Ihre E-Mail Adresse darf nicht mehr als 255 Zeichen enthalten.');

const rentAmountSchema = yup.string()
    .typeError(rentFormatMessage)
    .test(
        'max-digits-after-comma',
        maxDigitsMessage,
        value => {
            const splitted = value.split(',');
            return splitted.length === 1 || splitted[1].length <= 2;
        })
    .required('Dieser Wert sollte nicht leer sein.')
    .test(
        'is-valid-decimal-separator',
        rentFormatMessage,
        (value) => !(value).match(/[.]/)
    )
    .test(
        'is-number',
        'Die Kaltmiete muss eine Zahl sein.',
        (value) => {
            const re = /^[0-9,.]*$/;
            return re.test(String(value));
        })
    .test(
        'is-max-value',
        'Die Nettokaltmiete darf maximal 20.000 Euro betragen.',
        (value) => {
            const numberValue = parseFloat(value.replace(',', '.'));
            return numberValue <= 20000;
        }
    );

export const schemaNext = yup.object()
    .shape({
        next_rentAmount: rentAmountSchema,
        next_depositAmount: depositAmountSchema,
        next_destAddress: yup.string()
            .required('Sie müssen entweder die Digitale Bürgschaft oder eine Adresse auswählen.'),
        next_moveInDate: yup.date()
            .typeError('Bitte geben Sie ein Datum im Format dd.mm.yyyy ein.')
            .required(
                'Bitte geben Sie das Datum des Mietbeginns laut Mietvertrag an.')
            .max(dayjs()
                    .add(3, 'month')
                    .toDate(),
                'Das Einzugsdatum sollte maximal 6 Monate in der Vergangenheit und 3 Monate in der Zukunft liegen.')
            .min(dayjs()
                    .subtract(6, 'month')
                    .toDate(),
                'Das Einzugsdatum sollte maximal 6 Monate in der Vergangenheit und 3 Monate in der Zukunft liegen.'),
        next_contractStart: yup.date()
            .typeError('Bitte geben Sie ein Datum im Format dd.mm.yyyy ein.')
            .when('next_moveInDate', function (moveInDate, schema) {
                const tenantStore = getTenantStore();
                moveInDate = tenantStore.formValues.next_moveInDate;
    
                if (moveInDate && dayjs(moveInDate).isBefore(dayjs().startOf('day').add(2, 'day'), 'day')) {
                    return schema.notRequired();
                } else {
                    return schema
                      .required('Bitte geben Sie das Datum des Vertragsbeginns an.')
                      .min(
                        dayjs().startOf('day').add(1, 'day'),
                        'Der frühstmögliche Vertragsbeginn ist morgen.'
                      )
                      .max(
                        dayjs().add(3, 'month').toDate(),
                        'Der Vertragsbeginn kann maximal 3 Monate in der Zukunft liegen.'
                      )
                      .max(moveInDate, 'Der Vertragsbeginn darf nicht nach dem Einzugsdatum liegen.');
                }
            }
        ),    
        next_propertyStreetName: streetNameSchema(
            'Bitte geben Sie den Straßennamen Ihrer neuen Wohnung an.'),
        next_propertyHouseNumber: houseNumberSchema(
            'Bitte geben Sie die Hausnummer Ihrer neuen Wohnung an.'),
        next_propertyAddressCode: yup.string()
            .required('Bitte geben Sie die Postleitzahl Ihrer neuen Wohnung an.')
            .min(5, 'Eine Postleitzahl besteht aus 5 Nummern.')
            .max(5, 'Eine Postleitzahl besteht aus 5 Nummern.')
            .matches(/[0-9]{5}/, 'Eine Postleitzahl besteht aus 5 Nummern.'),
        next_propertyCity: citySchema(
            'Bitte geben Sie die Stadt an, in die Sie ziehen.'),
        next_landlordName: yup.string()
            .required(
                'Bitte geben Sie den Firmennamen oder bei privaten Vermietern den Vor- und Nachnamen Ihres Vermieters an.')
            .max(100, 'Ihr Firmenname oder Vor- und Nachname darf nicht länger als 100 Zeichen sein.'),
        next_landlordEmail: landlordEmailSchema,
        next_landlordStreetName: yup.string()
            .when('next_destAddress', () => {
                const tenantStore = getTenantStore();
                const destination = tenantStore.formValues.next_destAddress;
                if (destination === DELIVERY_TYPE.LANDLORD) {
                    return streetNameSchema(
                        'Bitte geben Sie den Straßennamen Ihres Vermieters an.');
                } else {
                    return yup.string()
                        .nullable();
                }
            }),
        next_landlordHouseNumber: yup.string()
            .when('next_destAddress', () => {
                const tenantStore = getTenantStore();
                const destination = tenantStore.formValues.next_destAddress;
                if (destination === DELIVERY_TYPE.LANDLORD) {
                    return houseNumberSchema(
                        'Bitte geben Sie die Hausnummer Ihres Vermieters an.');
                } else {
                    return yup.string()
                        .nullable();
                }
            }),
        next_landlordAddressCode: yup.string()
            .when('next_destAddress', () => {
                const tenantStore = getTenantStore();
                const destination = tenantStore.formValues.next_destAddress;
                if (destination === DELIVERY_TYPE.LANDLORD) {
                    return yup.string()
                        .required('Bitte geben Sie die Postleitzahl Ihres Vermieters an.')
                        .min(5, 'Eine Postleitzahl besteht aus 5 Nummern.')
                        .max(5, 'Eine Postleitzahl besteht aus 5 Nummern.')
                        .matches(/[0-9]{5}/, 'Eine Postleitzahl besteht aus 5 Nummern.');
                } else {
                    return yup.string()
                        .nullable();
                }
            }),
        next_landlordCity: yup.string()
            .when('next_destAddress', () => {
                const tenantStore = getTenantStore();
                const destination = tenantStore.formValues.next_destAddress;
                if (destination === DELIVERY_TYPE.LANDLORD) {
                    return citySchema('Bitte geben Sie die Stadt Ihres Vermieters an.');
                } else {
                    return yup.string()
                        .nullable();
                }
            }),
    });
